import { Alarm } from '../types/alarm';

// 오디오 캐시 객체
const audioCache: { [key: string]: HTMLAudioElement } = {};

// 현재 재생 중인 오디오 객체들
let currentlyPlayingAudios: HTMLAudioElement[] = [];

export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const getCurrentDayOfWeek = (): number => {
  return new Date().getDay();
};

export const getAlarmOffset = (type: '1M' | '3M' | '5M'): number => {
  switch (type) {
    case '1M': return 1; // 1분 전
    case '3M': return 3; // 3분 전
    case '5M': return 5; // 5분 전
    default: return 0;
  }
};

// 알람 타입, 카테고리, 남은 시간(분)에 따라 올바른 알람 파일 경로 반환
export const getAlarmSound = (
  type: '1M' | '3M' | '5M',
  category: 'basic' | 'class' = 'basic',
  minutesBefore: number
): string => {
  const prefix = category === 'class' ? 'ALRAM_CLASS' : 'ALRAM_ACTIVITY';
  
  // 1분전 알람 (1M 타입)
  if (type === '1M') {
    return `/sounds/${prefix}_END.mp3`;
  }
  
  // 3분전 알람 (3M 타입)
  if (type === '3M') {
    if (minutesBefore === 3) {
      return `/sounds/${prefix}_START.mp3`;
    } else if (minutesBefore === 2) {
      return `/sounds/${prefix}_2.mp3`;
    } else if (minutesBefore === 1) {
      return `/sounds/${prefix}_END.mp3`;
    }
  }
  
  // 5분전 알람 (5M 타입)
  if (type === '5M') {
    if (minutesBefore === 5) {
      return `/sounds/${prefix}_START.mp3`;
    } else if (minutesBefore === 4) {
      return `/sounds/${prefix}_4.mp3`;
    } else if (minutesBefore === 3) {
      return `/sounds/${prefix}_3.mp3`;
    } else if (minutesBefore === 2) {
      return `/sounds/${prefix}_2.mp3`;
    } else if (minutesBefore === 1) {
      return `/sounds/${prefix}_END.mp3`;
    }
  }
  
  // 기본값 (fallback)
  return `/sounds/${prefix}_END.mp3`;
};

// 오디오 파일을 미리 로드하여 캐시에 저장
export const preloadAlarmSounds = async (): Promise<void> => {
  // 이전 캐시 키 형식('A', 'B', 'C', '1M', '3M', '5M') 정리
  const oldKeys = ['A', 'B', 'C', '1M', '3M', '5M'];
  oldKeys.forEach(key => {
    if (audioCache[key]) {
      delete audioCache[key];
    }
  });
  
  const soundTypes: ('1M' | '3M' | '5M')[] = ['1M', '3M', '5M'];
  const categories: ('basic' | 'class')[] = ['basic', 'class'];
  
  for (const type of soundTypes) {
    for (const category of categories) {
      // 각 타입에 맞는 시간 목록
      let minutesList: number[] = [];
      if (type === '1M') {
        minutesList = [1];
      } else if (type === '3M') {
        minutesList = [3, 2, 1];
      } else if (type === '5M') {
        minutesList = [5, 4, 3, 2, 1];
      }
      
      for (const minutesBefore of minutesList) {
        const cacheKey = `${type}_${category}_${minutesBefore}`;
        if (!audioCache[cacheKey]) {
          const soundPath = getAlarmSound(type, category, minutesBefore);
          const audio = new Audio(soundPath);
          
          // 파일이 없을 경우 브라우저 기본 알람 소리 사용
          // onerror 핸들러를 load() 전에 설정
          audio.onerror = () => {
            // 이전 파일 경로(Alarm_A.mp3 등)에 대한 에러는 무시
            if (soundPath.includes('Alarm_A') || soundPath.includes('Alarm_B') || soundPath.includes('Alarm_C')) {
              // 이전 파일 경로는 더 이상 사용하지 않으므로 에러 로그 출력 안 함
              return;
            }
            console.warn(`⚠️ 알람 소리 파일을 찾을 수 없습니다 (대체 소리 사용): ${soundPath}`);
            // 브라우저의 기본 알람 소리나 다른 대체 소리 사용
            audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
          };
          
          // 오디오 로드 완료를 기다림
          try {
            await new Promise((resolve, reject) => {
              const handleCanPlay = () => {
                audio.removeEventListener('error', handleError);
                resolve(undefined);
              };
              const handleError = () => {
                audio.removeEventListener('canplaythrough', handleCanPlay);
                // 에러가 발생해도 계속 진행 (onerror 핸들러가 대체 소리로 설정)
                resolve(undefined);
              };
              
              audio.addEventListener('canplaythrough', handleCanPlay, { once: true });
              audio.addEventListener('error', handleError, { once: true });
              audio.load();
            });
            audioCache[cacheKey] = audio;
            console.log(`✅ 알람 소리 캐시 저장: ${cacheKey} -> ${soundPath}`);
          } catch (error) {
            console.warn(`⚠️ 알람 소리 로드 실패 (대체 소리 사용): ${cacheKey} -> ${soundPath}`, error);
            // 에러가 발생해도 캐시에 저장 (에러 처리된 오디오)
            audioCache[cacheKey] = audio;
          }
        }
      }
    }
  }
};

export const playAlarmSound = (
  type: '1M' | '3M' | '5M',
  category: 'basic' | 'class' = 'basic',
  minutesBefore: number
): HTMLAudioElement => {
  const soundPath = getAlarmSound(type, category, minutesBefore);
  const cacheKey = `${type}_${category}_${minutesBefore}`;
  
  // 이전 캐시 키 형식('A', 'B', 'C', '1M', '3M', '5M')이 있으면 삭제 (한 번만 실행)
  const oldKeys = ['A', 'B', 'C', '1M', '3M', '5M'];
  oldKeys.forEach(oldKey => {
    if (audioCache[oldKey] && !audioCache[oldKey].src.includes('ALRAM_')) {
      delete audioCache[oldKey];
    }
  });
  
  // 캐시에 있는 오디오가 있으면 복사해서 반환 (1회 다운로드 후 캐시 활용)
  if (audioCache[cacheKey]) {
    const cachedAudio = audioCache[cacheKey];
    // 캐시된 오디오가 올바른 경로를 참조하는지 확인
    if (cachedAudio.src.includes('ALRAM_') || cachedAudio.src.includes('data:audio')) {
      // 캐시에서 복사하여 재생 (원본 오디오 객체는 재사용하지 않고 복사본 사용)
      const audio = new Audio(cachedAudio.src);
      
      // 에러 처리도 복사
      audio.onerror = cachedAudio.onerror;
      
      // 재생 중인 오디오 목록에 추가
      currentlyPlayingAudios.push(audio);
      
      // 오디오가 끝나면 목록에서 제거
      audio.addEventListener('ended', () => {
        const index = currentlyPlayingAudios.indexOf(audio);
        if (index > -1) {
          currentlyPlayingAudios.splice(index, 1);
        }
      });
      
      return audio;
    } else {
      // 잘못된 캐시 항목이면 삭제하고 새로 생성
      console.warn(`⚠️ 잘못된 캐시 항목 삭제: ${cacheKey}`);
      delete audioCache[cacheKey];
    }
  }
  
  // 캐시에 없으면 새로 생성
  const audio = new Audio(soundPath);
  
  // 파일이 없을 경우 브라우저 기본 알람 소리 사용
  audio.onerror = () => {
    // 이전 파일 경로(Alarm_A.mp3 등)에 대한 에러는 무시
    if (soundPath.includes('Alarm_A') || soundPath.includes('Alarm_B') || soundPath.includes('Alarm_C')) {
      // 이전 파일 경로는 더 이상 사용하지 않으므로 에러 로그 출력 안 함
      return;
    }
    console.warn(`⚠️ 알람 소리 파일을 찾을 수 없습니다 (대체 소리 사용): ${soundPath}`);
    // 브라우저의 기본 알람 소리나 다른 대체 소리 사용
    audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
  };
  
  // 재생 중인 오디오 목록에 추가
  currentlyPlayingAudios.push(audio);
  
  // 오디오가 끝나면 목록에서 제거
  audio.addEventListener('ended', () => {
    const index = currentlyPlayingAudios.indexOf(audio);
    if (index > -1) {
      currentlyPlayingAudios.splice(index, 1);
    }
  });
  
  return audio;
};

export const formatTime = (time: string): string => {
  return time;
};

export const getDayName = (dayIndex: number): string => {
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return days[dayIndex];
};

export const sortAlarmsByTime = (alarms: Alarm[]): Alarm[] => {
  return [...alarms].sort((a, b) => {
    const timeA = new Date(`2000-01-01T${a.time}:00`);
    const timeB = new Date(`2000-01-01T${b.time}:00`);
    return timeA.getTime() - timeB.getTime();
  });
};

export const isAlarmActiveToday = (alarm: Alarm): boolean => {
  const today = new Date().getDay();
  return alarm.days.includes(today);
};

// 모든 알람 소리를 정지하는 함수
export const stopAllAlarmSounds = (): void => {
  console.log('모든 알람 소리 정지 시도. 현재 재생 중인 오디오 개수:', currentlyPlayingAudios.length);
  
  currentlyPlayingAudios.forEach((audio, index) => {
    try {
      audio.pause();
      audio.currentTime = 0;
      console.log(`오디오 ${index} 정지됨`);
    } catch (error) {
      console.error(`오디오 ${index} 정지 중 오류:`, error);
    }
  });
  
  // 배열 초기화
  currentlyPlayingAudios = [];
  console.log('모든 알람 소리 정지 완료');
};

// 특정 알람 타입의 소리만 정지하는 함수
// 새로운 파일 구조에서는 파일명으로 타입을 추출할 수 없으므로,
// 모든 재생 중인 오디오를 정지 (타입별 구분 불가)
export const stopAlarmSoundByType = (type: '1M' | '3M' | '5M'): void => {
  console.log(`[${type}] 알람 소리 정지 시도. 현재 재생 중인 오디오 개수:`, currentlyPlayingAudios.length);
  
  // 새로운 파일 구조에서는 파일명으로 타입을 구분할 수 없으므로
  // 모든 재생 중인 오디오를 정지
  currentlyPlayingAudios.forEach((audio, index) => {
    try {
      audio.pause();
      audio.currentTime = 0;
      console.log(`[${type}] 오디오 ${index} 정지됨`);
    } catch (error) {
      console.error(`[${type}] 오디오 ${index} 정지 중 오류:`, error);
    }
  });
  
  // 배열 초기화
  currentlyPlayingAudios = [];
  
  console.log(`[${type}] 알람 소리 정지 완료`);
};

// 알람이 울려야 하는지 확인하고, 몇 분 전인지 반환 (null이면 울리지 않음)
export const getMinutesBeforeAlarm = (alarm: Alarm): number | null => {
  if (!alarm.isActive || !isAlarmActiveToday(alarm)) return null;
  
  const now = new Date();
  
  // 정확히 분 단위가 바뀌는 순간(초가 0초)에만 알람이 울리도록 체크
  if (now.getSeconds() !== 0) {
    return null;
  }
  
  const currentTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0, 0);
  
  const [hours, minutes] = alarm.time.split(':').map(Number);
  const alarmTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
  
  // 알람 시간이 이미 지났다면 다음 날로 설정
  if (alarmTime.getTime() < currentTime.getTime()) {
    alarmTime.setDate(alarmTime.getDate() + 1);
  }
  
  // 알람 시간까지 남은 시간 계산 (분 단위, 정확한 분 단위)
  const diffMs = alarmTime.getTime() - currentTime.getTime();
  const diffMinutes = Math.floor(diffMs / (60 * 1000));
  
  // 알람 타입에 따라 체크할 시간 목록
  let checkMinutes: number[] = [];
  
  if (alarm.type === '1M') {
    // 1분전 알람: 1분전에만 체크
    checkMinutes = [1];
  } else if (alarm.type === '3M') {
    // 3분전 알람: 3분전, 2분전, 1분전 체크
    checkMinutes = [3, 2, 1];
  } else if (alarm.type === '5M') {
    // 5분전 알람: 5분전, 4분전, 3분전, 2분전, 1분전 체크
    checkMinutes = [5, 4, 3, 2, 1];
  }
  
  // 현재 시간이 체크할 시간 중 하나와 정확히 일치하는지 확인
  if (checkMinutes.includes(diffMinutes)) {
    return diffMinutes;
  }
  
  return null;
};

// 기존 shouldAlarmRing 함수 (하위 호환성을 위해 유지)
export const shouldAlarmRing = (alarm: Alarm): boolean => {
  return getMinutesBeforeAlarm(alarm) !== null;
};
